/**
 * IdeaView - Idea detail page with tabs for assumptions, specification, and plans
 *
 * Tabs:
 * - Overview: Idea, status, key metrics
 * - Assumptions: AI-generated assumptions and their values
 * - Specification: Generated idea spec
 * - Plans: List of plans for this idea
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Text,
  Caption,
  LoadingSpinner,
  ErrorMessage,
  BackIcon,
  tokens,
} from '../design-system';
import { ArrowDownTrayIcon, ClockIcon, TrashIcon as HeroTrashIcon } from '@heroicons/react/24/outline';
import { useIdea, useIdeaPlans, useIdeaProgress, useDeleteIdea, useSubmitDiscovery, useConfirmPlayback, useGenerateAssumptions, useConfirmAssumptions, useGenerateRisks, useAcknowledgeRisks, useRetryIdea, usePreviewIdeaRename, useApplyIdeaRename } from '../hooks/api/useIdeas';
import { useGaps, useDismissGap, usePreviewGap, useResolveGap, useRunGapAnalysis } from '../hooks/api/useGaps';
import { useApp } from '../contexts/AppContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from '../contexts/ThemeContext';
import CreatePlanModal from '../components/CreatePlanModal';
import { ValueDiff } from '../components/diff/ValueDiff';
import UpdateSpecResultModal from '../components/github/UpdateSpecResultModal';
import PlanChatbot from '../components/PlanChatbot';
import ChatPanel from '../components/ChatPanel';
import HistorySidebar from '../components/HistorySidebar';
import UpgradeModal from '../components/UpgradeModal';
import IdeaRoadmap from '../components/IdeaRoadmap';
import DiscoveryFlow from '../components/DiscoveryFlow';
import OverviewContent from '../components/idea/OverviewContent';
import DiscoveryContent from '../components/idea/DiscoveryContent';
import GitHubTab from '../components/GitHubTab';
import DownloadCatalog from '../components/DownloadCatalog';
import { previewText, cleanName } from '../utils/textUtils';

// Cottagecore design tokens
const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

const SIDE_NAV_ITEMS = [
  { id: 'overview',   label: 'Overview',   icon: '◎' },
  { id: 'roadmap',    label: 'Roadmap',    icon: '◫' },
  { id: 'discovery',  label: 'Discovery',  icon: '✿' },
  { id: 'gaps',       label: 'Gaps',       icon: '?' },
  { id: 'download',   label: 'Download',   icon: '↓' },
  { id: 'github',     label: 'GitHub',     icon: '⎇' },
];

const SEVERITY_COLOR = { high: '#C14A33', medium: '#EA7C1F', low: '#6B7A3E' };
const TYPE_LABEL = {
  undefined_behavior: 'Missing behavior',
  ambiguous_specification: 'Ambiguous',
  missing_scope: 'Missing scope',
  contradiction: 'Contradiction',
};

const RunAnalysisButton = ({ onRun, isRunning }) => (
  <button
    onClick={onRun}
    disabled={isRunning}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '7px 14px',
      background: isRunning ? '#EEE2BF' : '#EA7C1F',
      color: isRunning ? '#A39068' : '#fff',
      border: 'none',
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 700,
      cursor: isRunning ? 'not-allowed' : 'pointer',
      fontFamily: '"Nunito", sans-serif',
      transition: 'background 0.15s',
    }}
  >
    {isRunning ? (
      <>
        <span style={{
          display: 'inline-block', width: 12, height: 12,
          border: '2px solid #BFAB79', borderTopColor: '#6E5A38',
          borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }} />
        Analyzing…
      </>
    ) : (
      <>↻ Run analysis</>
    )}
  </button>
);

// Maps operation type to a short badge label + colour
const OP_BADGE = {
  replace: { label: 'update', bg: '#E8F0E0', color: T.moss },
  append:  { label: 'add',    bg: '#FEF3E2', color: T.orange },
};

// Human-readable section names for the diff preview
const SECTION_LABELS = {
  executive_summary: 'Executive Summary',
  problem_statement: 'Problem Statement',
  target_users: 'Target Users',
  core_features: 'Core Features',
  user_journeys: 'User Journeys',
  success_metrics: 'Success Metrics',
  constraints: 'Constraints',
  assumptions: 'Assumptions',
  out_of_scope: 'Out of Scope',
  risks: 'Risks',
  open_questions: 'Open Questions',
};

const GapCard = ({ gap, ideaId, onDismiss, onResolved }) => {
  // phase: 'idle' | 'answering' | 'previewing' | 'applying'
  const [phase, setPhase] = useState('idle');
  const [answer, setAnswer] = useState('');
  const [preview, setPreview] = useState(null); // { reasoning, changes }
  const previewGap = usePreviewGap(ideaId);
  const resolveGap = useResolveGap(ideaId);
  const { showSuccess, showError } = useApp();

  const handlePreview = async () => {
    if (!answer.trim()) return;
    try {
      const result = await previewGap.mutateAsync({ gapId: gap.id, answer: answer.trim() });
      setPreview(result);
      setPhase('previewing');
    } catch (err) {
      showError(err?.message || 'Failed to preview changes');
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setPhase('applying');
    try {
      const result = await resolveGap.mutateAsync({
        gapId: gap.id,
        answer: answer.trim(),
        confirmed_changes: preview.changes,
      });
      const updated = result?.sections_updated || [];
      const msg = updated.length
        ? `Spec updated — ${updated.join(', ')}`
        : 'Gap resolved';
      showSuccess(msg);
      onResolved?.();
    } catch (err) {
      setPhase('previewing');
      showError(err?.message || 'Failed to apply changes');
    }
  };

  const isBusy = previewGap.isPending || phase === 'applying';

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.rule}`,
      borderLeft: `3px solid ${SEVERITY_COLOR[gap.severity] || T.inkMuted}`,
      borderRadius: T.r3,
      padding: '16px 18px',
      boxShadow: T.sh1,
      opacity: isBusy ? 0.7 : 1,
      transition: 'opacity 0.15s ease',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: T.mono,
            fontSize: 10,
            color: T.inkMuted,
            fontWeight: 700,
            marginBottom: 4,
            letterSpacing: '0.5px',
          }}>
            {TYPE_LABEL[gap.type] ?? gap.type}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, lineHeight: 1.45, marginBottom: 4 }}>
            {gap.title}
          </div>
          <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.5 }}>
            {gap.question}
          </div>

          {/* ── ANSWER PHASE ── */}
          {phase === 'answering' && (
            <div style={{ marginTop: 12 }}>
              <textarea
                rows={3}
                autoFocus
                placeholder="Answer in a sentence or two…"
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: T.paperSoft,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  color: T.ink,
                  fontSize: 13,
                  fontFamily: T.sans,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setPhase('idle'); setAnswer(''); }}
                  disabled={isBusy}
                  style={{
                    padding: '6px 12px', fontSize: 12, fontWeight: 700,
                    background: 'transparent', border: `1px solid ${T.rule}`,
                    borderRadius: T.r2, cursor: 'pointer', color: T.inkMuted, fontFamily: T.sans,
                  }}
                >Cancel</button>
                <button
                  disabled={!answer.trim() || isBusy}
                  onClick={handlePreview}
                  style={{
                    padding: '6px 12px', fontSize: 12, fontWeight: 700,
                    background: answer.trim() && !isBusy ? T.moss : T.paperDeep,
                    border: 'none', borderRadius: T.r2,
                    cursor: answer.trim() && !isBusy ? 'pointer' : 'default',
                    color: answer.trim() && !isBusy ? '#fff' : T.inkMuted,
                    fontFamily: T.sans,
                  }}
                >
                  {previewGap.isPending ? 'Thinking…' : 'Preview changes →'}
                </button>
              </div>
            </div>
          )}

          {/* ── PREVIEW / CONFIRM PHASE ── */}
          {(phase === 'previewing' || phase === 'applying') && preview && (
            <div style={{ marginTop: 12 }}>
              {preview.reasoning && (
                <div style={{
                  fontSize: 13, color: T.ink2, lineHeight: 1.5,
                  marginBottom: 10, fontStyle: 'italic',
                }}>
                  {preview.reasoning}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {preview.changes.map((change, i) => {
                  const badge = OP_BADGE[change.operation] || { label: change.operation, bg: T.paperDeep, color: T.inkMuted };
                  return (
                    <div key={i} style={{
                      background: T.paperSoft,
                      border: `1px solid ${T.rule}`,
                      borderRadius: T.r2,
                      padding: 12,
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'baseline', gap: 8,
                        flexWrap: 'wrap', marginBottom: 8,
                      }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, fontFamily: T.mono,
                          background: badge.bg, color: badge.color,
                          padding: '2px 6px', borderRadius: 4,
                          textTransform: 'uppercase', flexShrink: 0,
                        }}>{badge.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.ink, flexShrink: 0 }}>
                          {SECTION_LABELS[change.section] || change.section}
                        </span>
                        {change.summary && (
                          <span style={{ fontSize: 12, color: T.inkMuted, fontStyle: 'italic' }}>
                            — {change.summary}
                          </span>
                        )}
                      </div>
                      <ValueDiff
                        oldValue={change.old_value}
                        newValue={change.new_value !== undefined ? change.new_value : change.value}
                      />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setPhase('answering')}
                  disabled={phase === 'applying'}
                  style={{
                    padding: '6px 12px', fontSize: 12, fontWeight: 700,
                    background: 'transparent', border: `1px solid ${T.rule}`,
                    borderRadius: T.r2, cursor: 'pointer', color: T.inkMuted, fontFamily: T.sans,
                  }}
                >← Back</button>
                <button
                  onClick={handleConfirm}
                  disabled={phase === 'applying'}
                  style={{
                    padding: '6px 12px', fontSize: 12, fontWeight: 700,
                    background: phase === 'applying' ? T.paperDeep : T.moss,
                    border: 'none', borderRadius: T.r2,
                    cursor: phase === 'applying' ? 'default' : 'pointer',
                    color: phase === 'applying' ? T.inkMuted : '#fff',
                    fontFamily: T.sans,
                  }}
                >
                  {phase === 'applying' ? 'Applying…' : '✓ Confirm changes'}
                </button>
              </div>
            </div>
          )}

          {/* ── IDLE PHASE ── */}
          {phase === 'idle' && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button
                onClick={() => setPhase('answering')}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 700,
                  background: T.orange, border: 'none', borderRadius: T.r2,
                  cursor: 'pointer', color: '#fff', fontFamily: T.sans,
                }}
              >Answer inline</button>
              <button
                onClick={() => onDismiss(gap.id)}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 700,
                  background: 'transparent', border: `1px solid ${T.rule}`,
                  borderRadius: T.r2, cursor: 'pointer', color: T.inkMuted, fontFamily: T.sans,
                }}
              >Dismiss</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GapsPanel = ({ gaps, ideaId, onDismiss, onResolved, onRun, isRunning }) => {
  if (gaps.length === 0) {
    return (
      <div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: T.ink, margin: '0 0 22px', letterSpacing: -0.6, fontFamily: T.sans }}>Gaps</h1>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '48px 24px', textAlign: 'center',
          background: T.card, border: `1px solid ${T.rule}`, borderRadius: T.r4, boxShadow: T.sh1,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🍄</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, marginBottom: 6, fontFamily: T.sans }}>All clear.</div>
          <div style={{ fontSize: 14, color: T.inkMuted, maxWidth: 360, lineHeight: 1.6, marginBottom: 20, fontFamily: T.sans }}>
            No open questions right now. We'll re-check whenever the idea changes.
          </div>
          <RunAnalysisButton onRun={onRun} isRunning={isRunning} />
        </div>
      </div>
    );
  }

  const byGroup = { high: [], medium: [], low: [] };
  gaps.forEach(g => (byGroup[g.severity] ?? byGroup.low).push(g));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22 }}>
        <div>
          <div style={{ fontFamily: T.hand, fontSize: 22, color: T.orange, lineHeight: 1 }}>questions —</div>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: T.ink, margin: '4px 0 6px', letterSpacing: -0.6, fontFamily: T.sans }}>Gaps</h1>
          <p style={{ fontSize: 14, color: T.ink2, margin: 0, fontFamily: T.sans }}>What's ambiguous in your plan. Answering a gap weaves the response back into the spec.</p>
        </div>
        <RunAnalysisButton onRun={onRun} isRunning={isRunning} />
      </div>

      {['high', 'medium', 'low'].map(severity => {
        const group = byGroup[severity];
        if (group.length === 0) return null;
        return (
          <div key={severity} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: SEVERITY_COLOR[severity], display: 'inline-block' }}/>
              <div style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: SEVERITY_COLOR[severity] }}>
                {severity} priority · {group.length}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {group.map(gap => (
                <GapCard key={gap.id} gap={gap} ideaId={ideaId} onDismiss={onDismiss} onResolved={onResolved} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};


const IdeaView = () => {
  const { ideaId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useApp();
  const { canUseChat, subscriptionLoading } = useSubscription();
  const { isDark } = useTheme();

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  // Short-lived polling flag set after scoping submit to catch background plan creation
  const [awaitingPlans, setAwaitingPlans] = useState(false);

  // Inline title editing state
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [renamePreview, setRenamePreview] = useState(null);
  const [renamePreviewError, setRenamePreviewError] = useState(null);
  const previewRenameMutation = usePreviewIdeaRename(ideaId);
  const applyRenameMutation = useApplyIdeaRename(ideaId);

  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => { prev.set('tab', tab); return prev; }, { replace: true });
  };

  // ── Title rename ─────────────────────────────────────────────────────────
  const handleStartTitleEdit = (currentTitle) => {
    setTitleDraft(currentTitle || '');
    setEditingTitle(true);
  };

  const handleCancelTitleEdit = () => {
    setEditingTitle(false);
    setTitleDraft('');
  };

  const handleSubmitTitleEdit = () => {
    const trimmed = titleDraft.trim();
    if (!trimmed) return;
    setEditingTitle(false);
    setRenamePreview(null);
    setRenamePreviewError(null);
    previewRenameMutation.mutate(
      { newName: trimmed },
      {
        onSuccess: (data) => setRenamePreview(data),
        onError: (e) => setRenamePreviewError(e),
      }
    );
  };

  const handleApplyRename = (approvedCascades) => {
    if (!renamePreview) return;
    const newName = renamePreview?.rename?.new_name || titleDraft.trim();
    const specEdits = renamePreview?.rename?.spec_edits || [];
    applyRenameMutation.mutate(
      {
        newName,
        approvedCascades,
        confirmedSpecEdits: specEdits.map((e) => ({ section: e.section, new_value: e.new_value })),
      },
      {
        onSuccess: (data) => {
          showSuccess(
            data?.applied_cascade_count
              ? `Renamed to "${data.new_name}" · ${data.applied_cascade_count} cascade${data.applied_cascade_count === 1 ? '' : 's'} applied`
              : `Renamed to "${data?.new_name}"`
          );
          setRenamePreview(null);
          setTitleDraft('');
        },
        onError: (e) => {
          showError(e?.response?.data?.detail || e?.message || 'Could not rename idea.');
        },
      }
    );
  };

  const handleCloseRenameModal = () => {
    if (applyRenameMutation.isPending) return;
    setRenamePreview(null);
    setRenamePreviewError(null);
    setTitleDraft('');
  };

  // Sync activeTab when the ?tab= param changes (e.g. navigate() calls from child components)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // If returning from GitHub App install, open the GitHub tab
  useEffect(() => {
    if (searchParams.get('github_connected') === 'true') {
      handleSetActiveTab('github');
      searchParams.delete('github_connected');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [chatWidth, setChatWidth] = useState(0);

  // Delete idea mutation
  const deleteIdeaMutation = useDeleteIdea();
  const handleDeleteIdea = async () => {
    const ideaName = idea?.name || idea?.idea?.substring(0, 50) || 'this idea';
    if (!window.confirm(`Are you sure you want to delete "${ideaName}"? This will also delete all associated plans.`)) {
      return;
    }
    try {
      await deleteIdeaMutation.mutateAsync(ideaId);
      showSuccess('Idea deleted');
      navigate('/dashboard');
    } catch (error) {
      showError(error.message || 'Failed to delete idea');
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch idea data
  const {
    data: idea,
    isLoading: ideaLoading,
    error: ideaError,
    refetch: refetchIdea,
  } = useIdea(ideaId);

  // Fetch plans for this idea — poll briefly after scoping submit to catch background creation
  const {
    data: plansData,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = useIdeaPlans(ideaId, { refetchInterval: awaitingPlans ? 1500 : false });


  const retryMutation = useRetryIdea();

  // Discovery mutations
  const confirmPlaybackMutation = useConfirmPlayback();
  const generateAssumptionsMutation = useGenerateAssumptions();
  const confirmAssumptionsMutation = useConfirmAssumptions();
  const generateRisksMutation = useGenerateRisks();
  const acknowledgeRisksMutation = useAcknowledgeRisks();
  const submitDiscoveryMutation = useSubmitDiscovery();

  // Local state for data returned from generate calls
  const [discoveryAssumptions, setDiscoveryAssumptions] = useState(null);
  const [discoveryRisks, setDiscoveryRisks] = useState(null);

  // Gap detection hooks
  const { data: gapsData } = useGaps(ideaId);
  const gaps = gapsData?.gaps ?? [];
  const gapCount = gapsData?.total ?? 0;
  const dismissGap = useDismissGap(ideaId);
  const runGapAnalysis = useRunGapAnalysis(ideaId);

  // Poll progress when processing
  const isProcessing = idea?.processing_stage && !['idle', 'error', 'complete'].includes(idea.processing_stage);
  const isError = idea?.processing_stage === 'error';
  const { data: progressData } = useIdeaProgress(ideaId, { polling: isProcessing });

  // Show discovery flow when the idea has a playback and is still in the discovery phases.
  // spec_review = spec generating in background; scoping = spec ready, user assigns features to phases.
  // No !idea_spec guard — scoping phase intentionally has spec present.
  const needsDiscovery = !isProcessing
    && idea?.playback != null
    && ['playback', 'assumptions', 'risks', 'spec_review', 'scoping'].includes(idea?.current_phase);

  // Discovery handlers
  const handlePlaybackConfirm = async (playback) => {
    try {
      await confirmPlaybackMutation.mutateAsync({ ideaId, playback });
      // After confirming playback, generate assumptions
      const result = await generateAssumptionsMutation.mutateAsync(ideaId);
      setDiscoveryAssumptions(result.assumptions);
      refetchIdea();
    } catch (err) {
      showError(err.userMessage || 'Failed to confirm playback');
    }
  };

  const handleAssumptionsConfirm = async (assumptions) => {
    try {
      await confirmAssumptionsMutation.mutateAsync({ ideaId, assumptions });
      // After confirming assumptions, generate risks
      const result = await generateRisksMutation.mutateAsync(ideaId);
      setDiscoveryRisks(result.risks);
      refetchIdea();
    } catch (err) {
      showError(err.userMessage || 'Failed to confirm assumptions');
    }
  };

  const handleRisksAcknowledge = async (risks) => {
    try {
      await acknowledgeRisksMutation.mutateAsync({ ideaId, risks });
      // Spec generation has started in the background (phase → "spec_review").
      // Refetch so the UI transitions to the spec_review loading state.
      refetchIdea();
    } catch (err) {
      showError(err.userMessage || 'Failed to acknowledge risks');
    }
  };

  const handleScopingConfirm = async (phases, features, existingFeatures) => {
    try {
      await submitDiscoveryMutation.mutateAsync({ ideaId, phases, features, existing_features: existingFeatures });
      await refetchIdea();
      // Plans are created in a background task — poll until they appear (max 30s)
      setAwaitingPlans(true);
      const deadline = Date.now() + 30000;
      const stop = setInterval(async () => {
        const result = await refetchPlans();
        const count = result.data?.items?.length ?? 0;
        if (count > 0 || Date.now() > deadline) {
          clearInterval(stop);
          setAwaitingPlans(false);
        }
      }, 1500);
    } catch (err) {
      showError(err.userMessage || 'Failed to submit discovery');
    }
  };

  useEffect(() => {
    if (progressData?.processing_stage === 'complete' || progressData?.processing_stage === 'idle') {
      refetchIdea();
      refetchPlans();
    }
  }, [progressData, refetchIdea, refetchPlans]);

  // Handle errors
  useEffect(() => {
    if (ideaError) {
      showError(ideaError.message || 'Failed to load idea');
    }
  }, [ideaError, showError]);

  const plans = plansData?.items || [];

  // Loading state - skeleton
  if (ideaLoading) {
    return (
      <div className="container" style={{ paddingTop: '16px', backgroundColor: T.paper, minHeight: '100vh' }}>
        {/* Skeleton header */}
        <div style={{ marginBottom: '16px' }}>
          <div className="skeleton-pulse" style={{
            height: '12px',
            width: '120px',
            backgroundColor: T.rule,
            borderRadius: T.r2,
            marginBottom: '12px',
          }} />
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
            <div>
              <div className="skeleton-pulse" style={{
                height: '28px',
                width: '250px',
                backgroundColor: T.rule,
                borderRadius: T.r2,
                marginBottom: '8px',
              }} />
              <div className="skeleton-pulse" style={{
                height: '14px',
                width: '400px',
                backgroundColor: T.rule,
                borderRadius: T.r2,
              }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[90, 100, 130].map((w, i) => (
                <div key={i} className="skeleton-pulse" style={{
                  height: '36px',
                  width: `${w}px`,
                  backgroundColor: T.rule,
                  borderRadius: T.r3,
                }} />
              ))}
            </div>
          </div>
        </div>
        {/* Skeleton tabs */}
        <div style={{
          display: 'flex',
          gap: '16px',
          borderBottom: `1px dashed ${T.ruleStrong}`,
          marginBottom: '24px',
          paddingBottom: '8px',
        }}>
          {[90, 90, 120, 90].map((w, i) => (
            <div key={i} className="skeleton-pulse" style={{
              height: '36px',
              width: `${w}px`,
              backgroundColor: T.rule,
              borderRadius: T.r2,
            }} />
          ))}
        </div>
        {/* Skeleton content cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {[1, 2].map((i) => (
            <div key={i} className="skeleton-pulse" style={{
              height: '136px',
              backgroundColor: T.card,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r4,
            }} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (ideaError || !idea) {
    return (
      <div className="container" style={{ paddingTop: '24px' }}>
        <ErrorMessage
          title="Idea not found"
          message={ideaError?.message || 'This idea may have been deleted or you may not have access.'}
          onRetry={() => refetchIdea()}
        />
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={(() => {
      const rightPad = Math.max(chatWidth, showHistoryPanel && !isMobile ? 360 : 0);
      return rightPad > 0 ? { paddingRight: `${rightPad}px` } : undefined;
    })()}>
    {/* PROCESSING / DISCOVERY MODE — full-width layout, no side nav */}
    {(isProcessing || needsDiscovery) && (
    <div className="container fade-in" style={{ paddingTop: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <Button variant="ghost" size="sm" icon={BackIcon} onClick={() => navigate('/dashboard')}>
          Dashboard
        </Button>
        <Text style={{ fontWeight: 600, color: T.ink, fontFamily: T.sans }}>
          {cleanName(idea.name) || 'Untitled Idea'}
        </Text>
      </div>

      {/* Error state — background task failed, offer retry */}
      {isError && !isProcessing && (
        <Card padding="lg" style={{ borderLeft: `4px solid ${T.capRed}`, marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <Text style={{ fontWeight: 600, marginBottom: '4px' }}>
                Something went wrong
              </Text>
              <Caption style={{ color: T.inkMuted }}>
                {(idea.progress_messages || []).slice(-1)[0]?.message || 'An error occurred during generation.'}
              </Caption>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => retryMutation.mutate(ideaId)}
              disabled={retryMutation.isPending}
            >
              {retryMutation.isPending ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        </Card>
      )}

      {/* Processing View - shows when generating */}
      {isProcessing && (() => {
        const isDiscoveryStage = ['generating_playback', 'generating_assumptions', 'generating_risks'].includes(idea.processing_stage);
        const isSpecStage = idea.processing_stage === 'generating_spec';
        const isPlansStage = idea.processing_stage === 'generating_plans';
        const stages = [
          { key: 'playback', label: 'Analyzing', desc: 'Understanding your idea' },
          { key: 'spec', label: 'Generating', desc: 'Writing the specification' },
          { key: 'plans', label: 'Planning', desc: 'Mapping features & stories' },
        ];
        const currentStageIdx = isDiscoveryStage ? 0 : isSpecStage ? 1 : isPlansStage ? 2 : 0;
        const latestMessages = progressData?.progress_messages?.slice(-6) || [];

        return (
          <div style={{
            maxWidth: 560,
            margin: '40px auto 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Idea title + (small) description */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: T.hand,
                fontSize: '1.5rem',
                color: T.orange,
                marginBottom: '8px',
                lineHeight: 1,
              }}>
                ✦
              </div>
              <div style={{
                fontFamily: T.hand,
                fontSize: 18,
                color: T.orange,
                lineHeight: 1,
                marginBottom: 4,
              }}>
                your idea —
              </div>
              <h1 style={{
                fontFamily: T.sans,
                fontSize: '1.75rem',
                fontWeight: 800,
                color: T.ink,
                letterSpacing: '-0.4px',
                margin: '0 0 8px',
                lineHeight: 1.2,
              }}>
                {cleanName(idea.name) || 'Untitled Idea'}
              </h1>
              {idea.idea && (
                <p style={{
                  fontFamily: T.sans,
                  fontSize: 13,
                  color: T.inkMuted,
                  lineHeight: 1.5,
                  margin: '0 auto',
                  maxWidth: 460,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {previewText(idea.idea, 160)}
                </p>
              )}
            </div>

            {/* Main card */}
            <div style={{
              background: T.card,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r4,
              overflow: 'hidden',
              boxShadow: T.sh2,
            }}>
              {/* Header strip */}
              <div style={{
                background: T.paperDeep,
                borderBottom: `1px dashed ${T.rule}`,
                padding: '16px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <LoadingSpinner size="sm" />
                <div>
                  <div style={{
                    fontFamily: T.sans,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: T.ink,
                  }}>
                    Working on it&hellip;
                  </div>
                  <div style={{
                    fontFamily: T.sans,
                    fontSize: '0.78rem',
                    color: T.inkMuted,
                    marginTop: '1px',
                  }}>
                    Usually takes 30&ndash;60 seconds
                  </div>
                </div>
              </div>

              {/* Stage stepper */}
              <div style={{ padding: '20px 24px 0' }}>
                {stages.map((stage, i) => {
                  const isComplete = i < currentStageIdx;
                  const isCurrent = i === currentStageIdx;
                  const isUpcoming = i > currentStageIdx;
                  return (
                    <div key={stage.key} style={{ display: 'flex', gap: '14px', paddingBottom: i < stages.length - 1 ? '0' : '20px' }}>
                      {/* Icon + connector */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isComplete ? '0.85rem' : '0.75rem',
                          fontWeight: 700,
                          fontFamily: T.sans,
                          backgroundColor: isComplete ? T.moss : isCurrent ? T.orange : T.paper,
                          border: isUpcoming ? `2px dashed ${T.rule}` : 'none',
                          color: isComplete ? '#fff' : isCurrent ? '#fff' : T.inkFaint,
                          transition: 'all 0.3s ease',
                          flexShrink: 0,
                        }}>
                          {isComplete ? '✓' : i + 1}
                        </div>
                        {i < stages.length - 1 && (
                          <div style={{
                            width: 2,
                            flex: 1,
                            minHeight: 20,
                            background: isComplete
                              ? T.moss
                              : `repeating-linear-gradient(to bottom, ${T.rule} 0px, ${T.rule} 4px, transparent 4px, transparent 8px)`,
                            margin: '4px 0',
                          }} />
                        )}
                      </div>
                      {/* Label */}
                      <div style={{ paddingTop: '4px', paddingBottom: i < stages.length - 1 ? '16px' : '0' }}>
                        <div style={{
                          fontFamily: T.sans,
                          fontWeight: isCurrent ? 700 : 500,
                          fontSize: '0.875rem',
                          color: isUpcoming ? T.inkFaint : T.ink,
                          marginBottom: '2px',
                        }}>
                          {stage.label}
                        </div>
                        <div style={{
                          fontFamily: T.sans,
                          fontSize: '0.78rem',
                          color: isUpcoming ? T.inkFaint : T.inkMuted,
                        }}>
                          {stage.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Activity log */}
              {latestMessages.length > 0 && (
                <div style={{
                  borderTop: `1px dashed ${T.rule}`,
                  padding: '14px 24px',
                  background: T.paper,
                }}>
                  {latestMessages.map((msg, i) => {
                    const isLatest = i === latestMessages.length - 1;
                    return (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '8px',
                        padding: '2px 0',
                        opacity: isLatest ? 1 : 0.45,
                      }}>
                        <span style={{
                          color: isLatest ? T.orange : T.moss,
                          fontSize: '8px',
                          flexShrink: 0,
                          lineHeight: '1.6',
                        }}>●</span>
                        <span style={{
                          fontFamily: T.sans,
                          fontSize: '0.78rem',
                          color: isLatest ? T.ink2 : T.inkMuted,
                          lineHeight: 1.5,
                        }}>
                          {msg.message}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tagline */}
            <p style={{
              textAlign: 'center',
              fontFamily: T.hand,
              fontSize: '0.95rem',
              color: T.inkFaint,
              margin: 0,
            }}>
              Turning your idea into a full product spec
            </p>
          </div>
        );
      })()}

      {/* Discovery Flow */}
      {needsDiscovery && (
        <DiscoveryFlow
          idea={idea}
          onPlaybackConfirm={handlePlaybackConfirm}
          onAssumptionsConfirm={handleAssumptionsConfirm}
          onRisksAcknowledge={handleRisksAcknowledge}
          onScopingConfirm={handleScopingConfirm}
          isLoading={
            confirmPlaybackMutation.isPending ||
            generateAssumptionsMutation.isPending ||
            confirmAssumptionsMutation.isPending ||
            generateRisksMutation.isPending ||
            acknowledgeRisksMutation.isPending ||
            submitDiscoveryMutation.isPending
          }
          assumptions={discoveryAssumptions}
          risks={discoveryRisks}
        />
      )}

    </div>
    )}

    {/* NORMAL TAB MODE — side nav layout */}
    {!isProcessing && !needsDiscovery && (
    <div className="fade-in" style={{ display: 'flex' }}>

      {/* ── SIDE NAV ── */}
      <aside style={{
        width: 232,
        flexShrink: 0,
        borderRight: `1px dashed ${T.ruleStrong}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        backgroundColor: T.paperDeep,
        padding: '22px 14px',
      }}>
        {/* Wordmark */}
        <div style={{ padding: '2px 10px 20px' }}>
          <img
            src="/text-logo.png"
            alt="Cahoots"
            onClick={() => navigate('/ideas')}
            style={{ height: 24, objectFit: 'contain', display: 'block', cursor: 'pointer' }}
          />
        </div>

        {/* Back to Ideas */}
        <button
          onClick={() => navigate('/ideas')}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            width: '100%', padding: '7px 12px',
            background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.sans,
            fontSize: '0.8rem', fontWeight: 600, color: T.inkMuted,
            borderRadius: T.r2,
            marginBottom: 4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = T.ink; e.currentTarget.style.background = T.card; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = T.inkMuted; e.currentTarget.style.background = 'none'; }}
        >
          <BackIcon size={12} />
          <span>All ideas</span>
        </button>

        {/* Idea title */}
        <div style={{ padding: '8px 12px 12px', marginBottom: 4 }}>
          <div style={{
            fontWeight: 800,
            fontSize: '0.8rem',
            color: T.inkFaint,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontFamily: T.sans,
            marginBottom: 4,
          }}>Current idea</div>
          {editingTitle ? (
            <div>
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitTitleEdit();
                  if (e.key === 'Escape') handleCancelTitleEdit();
                }}
                placeholder="Idea title"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  fontFamily: T.sans,
                  color: T.ink,
                  background: T.paperSoft,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button
                  onClick={handleSubmitTitleEdit}
                  disabled={!titleDraft.trim() || previewRenameMutation.isPending}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: 11, fontWeight: 700,
                    background: titleDraft.trim() && !previewRenameMutation.isPending ? T.moss : T.paperDeep,
                    color: titleDraft.trim() && !previewRenameMutation.isPending ? '#fff' : T.inkMuted,
                    border: 'none', borderRadius: T.r2,
                    cursor: titleDraft.trim() && !previewRenameMutation.isPending ? 'pointer' : 'default',
                    fontFamily: T.sans,
                  }}
                >
                  {previewRenameMutation.isPending ? 'Previewing…' : 'Preview'}
                </button>
                <button
                  onClick={handleCancelTitleEdit}
                  style={{
                    padding: '4px 8px',
                    fontSize: 11, fontWeight: 700,
                    background: 'transparent', border: `1px solid ${T.rule}`,
                    color: T.inkMuted, borderRadius: T.r2, cursor: 'pointer',
                    fontFamily: T.sans,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => handleStartTitleEdit(cleanName(idea.name) || idea.name || '')}
              title="Click to rename"
              style={{
                fontWeight: 700,
                fontSize: '0.9rem',
                color: T.ink,
                lineHeight: 1.3,
                display: 'flex', alignItems: 'flex-start', gap: 6,
                cursor: 'pointer',
                fontFamily: T.sans,
              }}
            >
              <span style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                flex: 1,
              }}>
                {cleanName(idea.name) || 'Untitled Idea'}
              </span>
              <span
                aria-hidden
                style={{ fontSize: 11, color: T.inkFaint, marginTop: 2, flexShrink: 0 }}
              >
                ✎
              </span>
            </div>
          )}
        </div>

        <nav style={{ flex: 1 }}>
          {SIDE_NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => handleSetActiveTab(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                background: activeTab === item.id ? T.card : 'transparent',
                border: activeTab === item.id ? `1px solid ${T.rule}` : '1px solid transparent',
                borderRadius: T.r3,
                boxShadow: activeTab === item.id ? T.sh1 : 'none',
                color: activeTab === item.id ? T.ink : T.ink2,
                fontSize: '0.875rem',
                fontWeight: activeTab === item.id ? 600 : 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
                fontFamily: T.sans,
                marginBottom: '2px',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== item.id) e.currentTarget.style.color = T.ink;
              }}
              onMouseLeave={(e) => {
                if (activeTab !== item.id) e.currentTarget.style.color = T.ink2;
              }}
            >
              <span style={{
                fontSize: '15px',
                opacity: activeTab === item.id ? 1 : 0.65,
                color: activeTab === item.id ? T.orange : T.inkMuted,
                width: 16,
                textAlign: 'center',
                flexShrink: 0,
                fontFamily: 'inherit',
              }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.id === 'gaps' && gapCount > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 800, color: '#fff',
                  background: T.capRed,
                  borderRadius: T.full,
                  padding: '1px 7px',
                  flexShrink: 0,
                }}>{gapCount}</span>
              )}
              {item.id === 'github' && (
                <span style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: T.orange,
                  backgroundColor: `${T.orange}18`,
                  border: `1px solid ${T.orange}40`,
                  borderRadius: '4px',
                  padding: '1px 5px',
                  letterSpacing: '0.03em',
                  lineHeight: '16px',
                  flexShrink: 0,
                }}>Pro</span>
              )}
            </button>
          ))}
        </nav>

        {/* Edit with AI — prominent CTA */}
        <div style={{ paddingBottom: 8, marginTop: 8 }}>
          <button
            onClick={() => {
              if (!subscriptionLoading && !canUseChat()) { setUpgradeModalFeature('chat'); setShowUpgradeModal(true); return; }
              setShowChatPanel(!showChatPanel);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '8px 12px',
              background: showChatPanel ? T.card : T.paperSoft,
              border: `1px dashed ${T.ruleStrong}`,
              borderRadius: T.r3,
              cursor: 'pointer',
              fontFamily: T.sans,
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = T.card;
              e.currentTarget.style.borderColor = T.orange;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = showChatPanel ? T.card : T.paperSoft;
              e.currentTarget.style.borderColor = T.ruleStrong;
            }}
          >
            <span style={{
              fontSize: '16px',
              color: T.orange,
              flexShrink: 0,
              lineHeight: 1,
            }}>✦</span>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: T.ink,
                  lineHeight: '1.2',
                }}>Edit with AI</span>
                <span style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: T.orange,
                  backgroundColor: `${T.orange}18`,
                  border: `1px solid ${T.orange}40`,
                  borderRadius: '4px',
                  padding: '1px 5px',
                  letterSpacing: '0.03em',
                  lineHeight: '16px',
                  flexShrink: 0,
                }}>Hobbyist</span>
              </div>
              <div style={{
                fontSize: '11px',
                color: T.inkMuted,
                opacity: 0.9,
                lineHeight: '1.3',
                marginTop: '1px',
              }}>Features, scope, priorities</div>
            </div>
          </button>
        </div>

        <div style={{ borderTop: `1px dashed ${T.rule}`, paddingTop: 8 }}>
          {/* History */}
          <button
            onClick={() => setShowHistoryPanel(!showHistoryPanel)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              width: '100%', padding: '9px 12px',
              background: showHistoryPanel ? `${T.orange}12` : 'none',
              border: showHistoryPanel ? `1px solid ${T.orange}30` : '1px solid transparent',
              borderRadius: T.r3,
              cursor: 'pointer', fontFamily: T.sans,
              fontSize: '0.875rem', fontWeight: 500,
              color: showHistoryPanel ? T.orange : T.inkMuted,
              textAlign: 'left', marginBottom: 2,
            }}
            onMouseEnter={(e) => { if (!showHistoryPanel) { e.currentTarget.style.color = T.ink; e.currentTarget.style.background = T.card; } }}
            onMouseLeave={(e) => { if (!showHistoryPanel) { e.currentTarget.style.color = T.inkMuted; e.currentTarget.style.background = 'none'; } }}
          >
            <ClockIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
            <span>History</span>
          </button>

          {/* Delete */}
          <button
            onClick={handleDeleteIdea}
            disabled={deleteIdeaMutation.isPending}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              width: '100%', padding: '9px 12px',
              background: 'none', border: '1px solid transparent',
              borderRadius: T.r3,
              cursor: 'pointer', fontFamily: T.sans,
              fontSize: '0.875rem', fontWeight: 500,
              color: T.capRed, opacity: deleteIdeaMutation.isPending ? 0.4 : 0.65,
              textAlign: 'left',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = `${T.capRed}10`; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = deleteIdeaMutation.isPending ? '0.4' : '0.65'; e.currentTarget.style.background = 'none'; }}
          >
            <HeroTrashIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
            <span>Delete</span>
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: T.paper,
      }}>
        {isError && !isProcessing && (
          <div style={{ padding: '16px 24px 0', flexShrink: 0 }}>
            <Card padding="lg" style={{ borderLeft: `4px solid ${T.capRed}`, marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                  <Text style={{ fontWeight: 600, marginBottom: '4px' }}>
                    Something went wrong
                  </Text>
                  <Caption style={{ color: T.inkMuted }}>
                    {(idea.progress_messages || []).slice(-1)[0]?.message || 'An error occurred during generation.'}
                  </Caption>
                </div>
                <Button variant="primary" size="sm" onClick={() => retryMutation.mutate(ideaId)} disabled={retryMutation.isPending}>
                  {retryMutation.isPending ? 'Retrying...' : 'Retry'}
                </Button>
              </div>
            </Card>
          </div>
        )}
        <div
          key={activeTab}
          className="tab-fade-in"
          style={{
            flex: 1,
            overflow: activeTab === 'roadmap' ? 'hidden' : 'auto',
            padding: activeTab === 'roadmap'
              ? '16px'
              : '24px 24px',
          }}
        >
          {activeTab === 'overview' && (
            <OverviewContent idea={idea} plans={plans} gapCount={gapCount} onRefetch={() => { refetchIdea(); refetchPlans(); }} onCreatePlan={() => setShowCreatePlan(true)} isMobile={isMobile} />
          )}
          {activeTab === 'roadmap' && (
            <IdeaRoadmap
              idea={idea}
              plans={plans}
              plansLoading={plansLoading}
              onRefetch={refetchPlans}
            />
          )}
          {activeTab === 'discovery' && (
            <DiscoveryContent idea={idea} onRefetch={refetchIdea} />
          )}
          {activeTab === 'download' && (
            <DownloadCatalog ideaId={ideaId} idea={idea} onShowUpgrade={() => { setUpgradeModalFeature('export'); setShowUpgradeModal(true); }} />
          )}
          {activeTab === 'github' && (
            <GitHubTab ideaId={ideaId} idea={idea} onRefreshIdea={refetchIdea} onShowUpgrade={() => { setUpgradeModalFeature('github_integration'); setShowUpgradeModal(true); }} />
          )}
          {activeTab === 'gaps' && (
            <GapsPanel
              gaps={gaps}
              ideaId={ideaId}
              onDismiss={(gapId) => dismissGap.mutate(gapId)}
              onResolved={refetchIdea}
              onRun={() => runGapAnalysis.mutate()}
              isRunning={runGapAnalysis.isAnalyzing}
            />
          )}
        </div>
      </main>

    </div>
    )}

      {/* Chat Panel - shows when chat button is clicked */}
      <ChatPanel
        isOpen={showChatPanel}
        onClose={() => setShowChatPanel(false)}
        chatUrl={`/ideas/${ideaId}/chat`}
        isMobile={isMobile}
        onWidthChange={setChatWidth}
      >
        <PlanChatbot
          ideaId={ideaId}
          task={null}
          taskTree={null}
          idea={idea}
          onPlanUpdate={refetchIdea}
          embedded
        />
      </ChatPanel>

      {/* History Sidebar */}
      <HistorySidebar
        ideaId={ideaId}
        isOpen={showHistoryPanel}
        onClose={() => setShowHistoryPanel(false)}
        onRefetch={refetchIdea}
      />

      {/* Create Plan Modal */}
      <CreatePlanModal
        isOpen={showCreatePlan}
        onClose={() => setShowCreatePlan(false)}
        idea={idea}
        onSuccess={() => {
          refetchPlans();
          setShowCreatePlan(false);
        }}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeModalFeature}
      />

      {/* Rename preview/confirmation modal — same shape as drift Update spec */}
      <UpdateSpecResultModal
        preview={renamePreview}
        loading={previewRenameMutation.isPending && !renamePreview}
        applying={applyRenameMutation.isPending}
        error={renamePreviewError}
        onApply={handleApplyRename}
        onClose={handleCloseRenameModal}
      />
    </div>
  );
};

export default IdeaView;
